import { useState, type FormEvent } from "react";
import { categories } from "@/mocks/site";

const SUBMIT_ADDR = "https://readdy.ai/api/form/daij8njp14a1h3mr7570";

type Status = "idle" | "loading" | "success" | "error";

export default function TipForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "company_alt") return;
      if (typeof value === "string" && value.length > 0) {
        payload.append(key, value);
      }
    });

    const honeypot = String(formData.get("company_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("线索已收到，感谢你的信任。我们会在整理时对来源做匿名处理。");
      form.reset();
      return;
    }

    setStatus("loading");
    setMessage("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        setMessage("线索已收到，感谢你的信任。我们会在整理时对来源做匿名处理。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(serverMsg || "提交失败，请稍后重试，或直接发送邮件给我们。");
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，暂时无法提交，请稍后重试。");
    }
  };

  return (
    <form
      id="tip-submit-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="space-y-5"
      noValidate
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <label className="block">
          <span className="block text-sm font-medium text-foreground-800 mb-2">怎么称呼你</span>
          <input
            type="text"
            name="name"
            autoComplete="nickname"
            placeholder="昵称或姓名均可"
            className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-foreground-800 mb-2">
            联系方式（邮箱）
          </span>
          <input
            type="email"
            name="email"
            required
            autoComplete="email"
            placeholder="用于必要时向你核实细节"
            className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </label>
      </div>

      <label className="block">
        <span className="block text-sm font-medium text-foreground-800 mb-2">涉及的方向</span>
        <span className="relative block">
          <select
            name="category"
            defaultValue={categories[0].label}
            className="w-full appearance-none px-4 py-3 pr-10 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-950 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
          >
            {categories.map((category) => (
              <option key={category.key} value={category.label}>
                {category.label}
              </option>
            ))}
            <option value="其他">其他 / 不确定</option>
          </select>
          <i
            className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"
            aria-hidden="true"
          ></i>
        </span>
      </label>

      <label className="block">
        <span className="block text-sm font-medium text-foreground-800 mb-2">
          线索描述
          <span className="ml-2 text-xs font-normal text-foreground-500">
            尽量写清时间、环节和对方让你签的字（最多 500 字）
          </span>
        </span>
        <textarea
          name="detail"
          required
          rows={6}
          maxLength={500}
          placeholder="例如：2024 年 10 月，签合同前对方口头承诺「超出部分按 5% 内不加价」，开工后却……"
          className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 resize-y"
        ></textarea>
      </label>

      <div className="field-verify-wrap" aria-hidden="true">
        <input
          type="text"
          name="company_alt"
          tabIndex={-1}
          autoComplete="off"
          readOnly
          aria-hidden="true"
        />
      </div>

      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          name="anonymous_ok"
          defaultChecked
          className="mt-0.5 w-4 h-4 accent-primary-500"
        />
        <span className="text-xs text-foreground-600 leading-relaxed">
          我了解平台会对来源做匿名处理，并同意平台在不公开我身份的前提下整理线索。
        </span>
      </label>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <button
          type="submit"
          disabled={status === "loading"}
          className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <i className="ri-send-plane-line" aria-hidden="true"></i>
          {status === "loading" ? "提交中…" : "提交线索"}
        </button>
        <span className="text-xs text-foreground-500">
          我们不会公开你的联系方式，也不会把线索转给第三方。
        </span>
      </div>

      {message && (
        <p
          role="status"
          className={`text-sm rounded-md px-4 py-3 ${
            status === "success"
              ? "bg-primary-50 text-primary-700 border border-primary-200"
              : "bg-accent-50 text-accent-900 border border-accent-300"
          }`}
        >
          {message}
        </p>
      )}
    </form>
  );
}